import { initOAuthManager } from '../utils/oauthManager.js';
import { interpolate, getCurrentLanguage, loadTranslations, applyTranslationsToContainer } from '../translations.js';
import { hydratePricingFromPlans } from '../utils/pricingPlans.js';
import { isSfwSite } from '../config.js';

export class LoginModalController {
  constructor(options = {}) {
    if (LoginModalController.instance) {
      return LoginModalController.instance;
    }

    this.options = {
      modalId: 'loginModal',
      // Default to regular login; combined trial+login is opt-in at show()
      templateUrl: '/components/modals/login-modal.html',
      ...options
    };

    this.modal = null;
    this.oauthManager = null;
    this.isInitialized = false;
    this.isProcessing = false; // Debouncing flag
    this.lastRequestTime = 0; // Track last request time
    this.language = 'en';
    this._onLanguageChange = this._handleLanguageChange.bind(this);

    LoginModalController.instance = this;
  }

  async init() {
    if (this.isInitialized) return;

    try {
      // Ensure base login styles
      this._ensureCss('/css/modals.css');

      if (!document.getElementById(this.options.modalId)) {
        await this._loadModalHtml();
      }

      this.modal = document.getElementById(this.options.modalId);
      if (!this.modal) throw new Error('Modal not found');

      this.oauthManager = initOAuthManager();
      this._bindEvents();
      this.isInitialized = true;
    } catch (error) {
      console.error('Login init failed:', error);
      throw error;
    }
  }

  async _loadModalHtml() {
    const response = await fetch(this.options.templateUrl);
    if (!response.ok) throw new Error(`Template load failed: ${response.status}`);
    
    const html = await response.text();
    const host = document.createElement('div');
    host.innerHTML = html;
    [...host.children].forEach((node) => {
      if (node.tagName === 'SCRIPT' || node.tagName === 'STYLE') return;
      document.body.appendChild(node);
    });
  }

  _bindEvents() {
    const events = [
      // Close buttons (support both original and combined modal)
      { selector: '.close-login-modal', handler: () => this._trackCloseAndHide('close_button') },
      { selector: '.quota-pricing-close', handler: () => this._trackCloseAndHide('close_button') },
      // Magic link buttons (support both selectors)
      { selector: '.login-btn', handler: (e) => { this._markTrialIntent(); this._handleLogin(e); } },
      { selector: '.auth-login-btn', handler: (e) => { this._markTrialIntent(); this._handleLogin(e); } },
      // Social buttons
      { selector: '.twitter-login-btn', handler: () => { this._markTrialIntent(); this._handleSocial('twitter'); } },
      { selector: '.discord-login-btn', handler: () => { this._markTrialIntent(); this._handleSocial('discord'); } },
      { selector: '.google-login-btn', handler: () => { this._markTrialIntent(); this._handleSocial('google'); } }
    ];

    events.forEach(({ selector, handler }) => {
      const element = this.modal.querySelector(selector);
      if (element) element.addEventListener('click', handler);
    });

    this.modal.addEventListener('click', (e) => {
      if (e.target === this.modal) this._trackCloseAndHide('outside_click');
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isVisible()) this._trackCloseAndHide('escape_key');
    });
  }

  async _loadAndApplyTranslations(isTrial = false) {
    this.language = getCurrentLanguage();
    const namespaces = ['login', 'account'];
    if (isTrial) namespaces.push('pricing');
    await Promise.all(namespaces.map(ns => loadTranslations(ns, this.language)));
    if (!this.modal) return;
    namespaces.forEach(ns => applyTranslationsToContainer(this.modal, ns, this.language));
    if (isTrial) {
      await hydratePricingFromPlans(this.modal, this.language);
    }
  }

  async _handleLanguageChange(e) {
    if (e.detail?.language && e.detail.language !== this.language) {
      const isTrial = this.modal?.classList.contains('trial-auth-modal') ?? false;
      await this._loadAndApplyTranslations(isTrial);
    }
  }

  async _handleLogin(e) {
    e.preventDefault();

    // Debouncing: prevent multiple requests within 60 seconds
    const now = Date.now();
    if (this.isProcessing || (now - this.lastRequestTime) < 60000) {
      console.log('Magic link request debounced - too soon since last request');
      return;
    }

    const emailInput = this.modal.querySelector('.login-input') || this.modal.querySelector('.auth-login-input');
    const email = emailInput?.value?.trim();
    if (!email) return this.showMessage('Email required');

    const btn = e.target;
    const originalText = btn.textContent;

    this.isProcessing = true;
    this.lastRequestTime = now;
    btn.disabled = true;
    btn.style.cursor = 'default';
    btn.textContent = 'Sending...';

    try {
      await this.oauthManager.startMagicLinkAuth(email);
      const loginT = window.translations?.[this.language]?.login;
      this.showMessage(loginT?.magicLinkSuccessMessage || 'Great! Check your email 📨', 'success', 'upper');
      emailInput.value = '';
      btn.textContent = loginT?.sent || 'Sent!';

      // Start countdown after a brief delay to show success message
      setTimeout(() => this._startResendCountdown(btn, originalText), 7000);
    } catch (error) {
      const loginBundle = window.translations?.[this.language]?.login;
      const loginT = loginBundle?.login || loginBundle;
      const resetSendButton = () => {
        this.isProcessing = false;
        this.lastRequestTime = 0;
        btn.disabled = false;
        btn.style.cursor = '';
        btn.textContent = loginT?.sendMagicLink || originalText;
      };

      if (error?.message === 'EMAIL_TYPO_EDIT_REQUESTED') {
        emailInput?.focus();
        emailInput?.select?.();
        resetSendButton();
        return;
      }

      const message = error?.message === 'Invalid email address'
        ? (loginT?.magicLinkInvalidEmail || 'Invalid email format. Use an address like name@example.com.')
        : (loginT?.magicLinkSendFailed || "Couldn't send the link. Try a different email, or continue with Google or Discord.");
      this.showMessage(message);
      resetSendButton();
    }
  }

  _startResendCountdown(btn, originalText) {
    let remainingSeconds = 55;
    const updateCountdown = () => {
      if (remainingSeconds > 0) {
        const message = interpolate(window.translations?.[this.language]?.login?.magicLinkResendMessage || "Didn't receive the email? Check your spams, or try again in {seconds} seconds", { seconds: remainingSeconds });
        this.showMessage(message, 'info', 'lower');
        remainingSeconds--;
        this.countdownTimer = setTimeout(updateCountdown, 1000);
      } else {
        this.showMessage('', 'info', 'lower');
        btn.disabled = false;
        btn.style.cursor = '';
        btn.textContent = window.translations?.[this.language]?.login?.sendMagicLink || originalText;
        this.isProcessing = false;
      }
    };
    updateCountdown();
  }

  _ensureCss(href) {
    try {
      if (document.querySelector(`link[href="${href}"]`)) return;
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = href;
      document.head.appendChild(link);
    } catch (_) {
      // no-op
    }
  }

  _markTrialIntent() {
    try {
      if (this.modal?.classList?.contains('trial-auth-modal')) {
        sessionStorage.setItem('trial_login_intent', '1');
      }
    } catch (_) {}
  }

  async _handleSocial(provider) {
    try {
      const method = `start${provider[0].toUpperCase()}${provider.slice(1)}Auth`;
      await this.oauthManager[method]();
    } catch (error) {
      this.showMessage(`${provider} auth failed`);
    }
  }

  async show(useTrialAuth = false, message) {
    if (!this.isInitialized) {
      await this.init();
    }

    const trialAuth = !!useTrialAuth && isSfwSite();
    await this._ensureTemplateForVariant(trialAuth);
    if (!this.modal) return;

    await this._loadAndApplyTranslations(trialAuth);
    document.addEventListener('languageChanged', this._onLanguageChange);

    this.modal.style.display = 'flex';
    const isTrial = this.modal.classList.contains('trial-auth-modal');
    if (isTrial) {
      this._ensureCss('/css/modals/pricing-trial-auth.css');
      this.modal.classList.add('show');
      this.modal.style.opacity = '1';
      this.modal.style.pointerEvents = 'auto';
      // Ensure the trial image grid initializes and fetches from the worker
      try {
        const { initializeTrialImageGridFor } = await import('../utils/quotaErrorHandler.js');
        initializeTrialImageGridFor?.(this.modal);
      } catch (_) {}
    }
    if (message) this.showMessage(message);
  }

  _trackCloseAndHide() {
    this.hide();
  }

  hide() {
    if (!this.modal) return;

    document.removeEventListener('languageChanged', this._onLanguageChange);
    if (this.modal.classList && this.modal.classList.contains('trial-auth-modal')) {
      this.modal.classList.remove('show');
      setTimeout(() => { this.modal.style.display = 'none'; }, 200);
    } else {
      this.modal.style.display = 'none';
    }
    this.clearMessage();
  }

  toggle() {
    this.isVisible() ? this.hide() : this.show();
  }

  isVisible() {
    return this.modal?.style.display === 'flex';
  }

  showMessage(text, type = 'error', position = 'upper') {
    // Handle both regular login modal (.login-modal-message) and trial/free account modals (.auth-panel-message)
    let selector = '.login-modal-message';
    if (this.modal?.classList?.contains('trial-auth-modal')) {
      selector = position === 'upper' ? '.auth-panel-message.success-message' : '.auth-panel-message.countdown-message';
    }
    const el = this.modal?.querySelector(selector);
    if (!el) return;

    el.textContent = text;
    el.style.display = text ? 'block' : 'none';
    el.className = `${selector.slice(1).replace('.', ' ')} ${type}`;
  }

  clearMessage() {
    const el = this.modal?.querySelector('.login-modal-message');
    if (!el) return;
    
    el.style.display = 'none';
    el.textContent = '';
    el.className = 'login-modal-message';
  }

  destroy() {
    this.modal?.remove();
    this.modal = null;
    this.oauthManager = null;
    this.isInitialized = false;
    LoginModalController.instance = null;
  }

  static getInstance(options = {}) {
    if (!LoginModalController.instance) {
      new LoginModalController(options);
    }
    return LoginModalController.instance;
  }

  async _ensureTemplateForVariant(useTrialAuth) {
    // CSS for variant
    this._ensureCss(useTrialAuth ? '/css/modals/pricing-trial-auth.css' : '/css/modals.css');

    const existing = document.getElementById(this.options.modalId);
    const hasTrial = !!existing?.classList?.contains('trial-auth-modal');
    const needTrial = !!useTrialAuth;

    if (!existing || (needTrial && !hasTrial) || (!needTrial && hasTrial)) {
      try { existing?.remove(); } catch {}
      this.options.templateUrl = needTrial
        ? '/components/modals/trial-offer-login-modal.html'
        : '/components/modals/login-modal.html';
      await this._loadModalHtml();
      this.modal = document.getElementById(this.options.modalId);
      if (!this.modal) throw new Error('Modal not found');
      this._bindEvents();
    } else {
      this.modal = existing;
    }
  }
}

LoginModalController.instance = null;

export default LoginModalController.getInstance();
